import React from "react";
import {
  FormControl,
  FormLabel,
  TextField,
  Grid,
  GridProps,
} from "@mui/material";
import TooltipWithClickAway from "../../terminalPicker/TooltipWithClickAway";
import { FORM_STYLES, FORM_CLASSES, S_GRID_PROPS } from "../../../constants";
import {
  handleKeyDown,
  handleNumberMaxLength,
  handlePaste,
} from "../../../helpers";

export interface LabeledTextFieldProps {
  id: string;
  label: string;
  value?: string | number | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  type?: "text" | "number" | "email" | "file";
  accept?: string;
  maxLength?: number;
  gridProps?: GridProps;
  tooltip?: {
    text: string;
    key: string;
  };
  error?: boolean;
  helperText?: string;
  multiline?: boolean;
  rows?: number;
  endAdornment?: React.ReactNode;
}

/**
 * Standardized labeled text field component for consistent styling
 * across all form fields that need custom labels with tooltips
 */
const LabeledTextField: React.FC<LabeledTextFieldProps> = ({
  id,
  label,
  value = "",
  onChange,
  onBlur,
  placeholder,
  required = false,
  disabled = false,
  type = "text",
  maxLength,
  gridProps = S_GRID_PROPS,
  tooltip,
  error = false,
  helperText,
  multiline = false,
  rows = 1,
  endAdornment,
}) => {

  return (
    <Grid item {...gridProps}>
      <FormControl
        fullWidth
        size={FORM_STYLES.FIELD_SIZE}
        className={FORM_CLASSES.FORM_CONTROL}
      >
        <FormLabel
          id={`${id}Label`}
          htmlFor={`${id}-input`}
          className={FORM_CLASSES.LABEL_BASE}
          style={FORM_STYLES.LABEL}
        >
          {label} {required && <sup>*</sup>}
          {tooltip && (
            <TooltipWithClickAway
              tooltipText={tooltip.text}
              tooltipKey={tooltip.key}
            />
          )}
        </FormLabel>
        <TextField
          autoComplete="off"
          fullWidth
          id={id}
          name={id}
          value={type === "file" ? undefined : value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          type={type}
          multiline={multiline}
          data-testid={id}
          rows={rows}
          title={(value?.toString()?.length ?? 0) > 30 ? value?.toString() : undefined}
          inputProps={{
            maxLength,
            "data-testid": `${id}-input`,
            id: `${id}-input`,
            style: FORM_STYLES.INPUT,
          }}
          InputProps={endAdornment ? { endAdornment } : undefined}
          error={error}
          helperText={helperText}
          variant="outlined"
          size="small"
          {...(type === "number" && {
            onPaste: handlePaste,
            onKeyDown: handleKeyDown,
            onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleNumberMaxLength(e, maxLength);
            },
          })}
        />
      </FormControl>
    </Grid>
  );
};

export default LabeledTextField;
